import React, { useState, useRef, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  Search,
  User,
  Settings,
  LogOut,
  ChevronDown,
  Menu,
  CornerDownLeft,
} from 'lucide-react';
import { useTheme } from '../../context/ThemeContext';
import NotificationsMenu from './NotificationsMenu';
import { logout } from '../../store/slices/authSlice';
import { searchModules } from '../../config/modules';
import './Header.css';

const Header = ({ onMenuClick }) => {
  const { mode, setMode } = useTheme();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [logoutConfirmOpen, setLogoutConfirmOpen] = useState(false);
  const [modePopover, setModePopover] = useState(null); // 'confirm' | null
  const [search, setSearch] = useState('');
  const [searchOpen, setSearchOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const dropdownRef = useRef(null);
  const modeRef = useRef(null);
  const searchRef = useRef(null);
  const searchBoxRef = useRef(null);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { user } = useSelector((state) => state.auth);
  const isSuperAdmin = user?.role_id === 1 || user?.roleId === 1;

  // Modules matching what has been typed, best match first.
  const matches = useMemo(
    () => searchModules(search, isSuperAdmin),
    [search, isSuperAdmin]
  );

  const query = search.trim();
  // The last row always offers a free-text transaction lookup, so hashes and
  // emails still work even when no module name matches.
  const fallbackIndex = matches.length;
  const showSuggestions = searchOpen && query.length > 0;

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownOpen(false);
      }
      if (modeRef.current && !modeRef.current.contains(event.target)) {
        setModePopover(null);
      }
      if (searchBoxRef.current && !searchBoxRef.current.contains(event.target)) {
        setSearchOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // ⌘K / Ctrl+K focuses the search box.
  useEffect(() => {
    const onKeyDown = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        searchRef.current?.focus();
        setSearchOpen(true);
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  // Signing out is a deliberate action, so it asks first. Only THIS path
  // confirms: the 401 handler in DashboardLayout and SuspendedModal both end the
  // session involuntarily, where a prompt would be pointless (and unanswerable).
  const handleLogout = () => {
    setDropdownOpen(false);
    setLogoutConfirmOpen(true);
  };

  const confirmLogout = () => {
    setLogoutConfirmOpen(false);
    dispatch(logout());
    navigate('/login');
  };

  // Escape closes the dialog. The element carries aria-modal="true", which tells
  // assistive tech it is a modal, so a keyboard user reasonably expects Escape
  // to dismiss it. Bound only while open so it cannot swallow the key elsewhere.
  useEffect(() => {
    if (!logoutConfirmOpen) return undefined;

    const onKeyDown = (e) => {
      if (e.key === 'Escape') setLogoutConfirmOpen(false);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [logoutConfirmOpen]);

  const closeSearch = () => {
    setSearch('');
    setSearchOpen(false);
    setActiveIndex(0);
  };

  const goToModule = (module) => {
    navigate(module.path);
    closeSearch();
    searchRef.current?.blur();
  };

  const goToTransactionSearch = () => {
    const q = search.trim();
    navigate(
      q
        ? `/dashboard/transactions?search=${encodeURIComponent(q)}`
        : '/dashboard/transactions'
    );
    closeSearch();
    searchRef.current?.blur();
  };

  // Enter opens the highlighted suggestion; with nothing highlighted it falls
  // back to searching transactions for the raw text.
  const handleSearch = (e) => {
    e.preventDefault();
    if (!query) {
      goToTransactionSearch();
      return;
    }
    if (activeIndex < matches.length) {
      goToModule(matches[activeIndex]);
      return;
    }
    goToTransactionSearch();
  };

  const handleSearchKeyDown = (e) => {
    if (e.key === 'Escape') {
      setSearchOpen(false);
      searchRef.current?.blur();
      return;
    }
    if (!showSuggestions) return;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex((i) => (i >= fallbackIndex ? 0 : i + 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex((i) => (i <= 0 ? fallbackIndex : i - 1));
    }
  };

  // Test → switch instantly. Live → confirm first (or prompt to activate if gated).
  const handleSelectMode = (next) => {
    if (next === mode) return;
    if (next === 'test') {
      setMode('test');
      setModePopover(null);
      return;
    }
    // The Test/Live switch is a view change, so it always offers the
    // confirmation rather than an activation block. Live API key creation is
    // still gated on activation — that gate lives in Developer.jsx.
    setModePopover('confirm');
  };

  const confirmGoLive = () => {
    setMode('live');
    setModePopover(null);
  };

  const getUserName = () => {
    if (!user) return 'User';
    return user.firstName || user.first_name || 'User';
  };

  const getUserEmail = () => {
    if (!user) return '';
    return user.email || '';
  };

  return (
    <header className="dashboard-header">
      <div className="header-left">
        <button
          type="button"
          className="icon-btn hamburger-btn"
          aria-label="Open menu"
          onClick={onMenuClick}
        >
          <Menu size={22} />
        </button>
      </div>

      <form
        className="header-search"
        onSubmit={handleSearch}
        role="search"
        ref={searchBoxRef}
      >
        <Search size={17} className="header-search-icon" />
        <input
          ref={searchRef}
          type="text"
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setSearchOpen(true);
            setActiveIndex(0);
          }}
          onFocus={() => setSearchOpen(true)}
          onKeyDown={handleSearchKeyDown}
          placeholder="Search transactions, invoices, settings..."
          aria-label="Search"
          autoComplete="off"
          role="combobox"
          aria-expanded={showSuggestions}
          aria-controls="header-search-results"
        />
        <span className="header-search-kbd" aria-hidden="true">⌘K</span>

        {showSuggestions && (
          <div
            className="header-search-results"
            id="header-search-results"
            role="listbox"
          >
            {matches.length > 0 ? (
              matches.map((module, index) => (
                <button
                  key={module.path}
                  type="button"
                  role="option"
                  aria-selected={index === activeIndex}
                  className={`header-search-result ${index === activeIndex ? 'active' : ''}`}
                  onMouseEnter={() => setActiveIndex(index)}
                  onClick={() => goToModule(module)}
                >
                  <module.icon size={16} className="header-search-result-icon" />
                  <span className="header-search-result-label">{module.label}</span>
                  <span className="header-search-result-group">{module.group}</span>
                </button>
              ))
            ) : (
              <div className="header-search-empty">
                No modules match "{query}"
              </div>
            )}

            <button
              type="button"
              role="option"
              aria-selected={activeIndex === fallbackIndex}
              className={`header-search-result header-search-fallback ${activeIndex === fallbackIndex ? 'active' : ''}`}
              onMouseEnter={() => setActiveIndex(fallbackIndex)}
              onClick={goToTransactionSearch}
            >
              <CornerDownLeft size={16} className="header-search-result-icon" />
              <span className="header-search-result-label">
                Search transactions for "{query}"
              </span>
            </button>
          </div>
        )}
      </form>

      <div className="header-right">
        <div className="mode-toggle-wrap" ref={modeRef}>
          <div className="mode-toggle" data-mode={mode}>
            <button
              className={`mode-btn ${mode === 'test' ? 'active' : ''}`}
              onClick={() => handleSelectMode('test')}
            >
              Test
            </button>
            <button
              className={`mode-btn ${mode === 'live' ? 'active' : ''}`}
              onClick={() => handleSelectMode('live')}
            >
              Live
            </button>
            <div className={`mode-slider ${mode === 'live' ? 'live' : ''}`} />
          </div>

          {modePopover === 'confirm' && (
            <div className="mode-popover" role="dialog" aria-label="Switch to Live mode">
              <h4>Switch to Live mode?</h4>
              <p>You'll see real payments and balances. Live transactions move real funds — make sure your integration uses your <strong>live</strong> API keys.</p>
              <div className="mode-popover-actions">
                <button className="rd-btn rd-btn-secondary rd-btn-sm" onClick={() => setModePopover(null)}>Cancel</button>
                <button className="rd-btn rd-btn-primary rd-btn-sm" onClick={confirmGoLive}>Switch to Live</button>
              </div>
            </div>
          )}

        </div>

        {/* Live feed + bell badge; owns its own SSE stream and popover state. */}
        <NotificationsMenu />

        <div className="profile-dropdown" ref={dropdownRef}>
          <button
            className="profile-btn"
            onClick={() => setDropdownOpen(!dropdownOpen)}
          >
            <div className="avatar">
              {getUserName().charAt(0).toUpperCase()}
            </div>
            <div className="profile-info">
              <span className="profile-name">{getUserName()}</span>
              <span className="profile-email">{getUserEmail()}</span>
            </div>
            <ChevronDown size={16} className={dropdownOpen ? 'rotate' : ''} />
          </button>

          {dropdownOpen && (
            <div className="dropdown-menu">
              {/*
                Both of these used to navigate to the bare '/dashboard/settings'.
                That made Profile a no-op whenever Settings was already open --
                same route, so nothing re-rendered and the visible tab did not
                move. Profile now pins the tab explicitly via ?tab=, which
                Settings reads and keeps in sync.
              */}
              <button
                className="dropdown-item"
                onClick={() => {
                  navigate('/dashboard/settings?tab=profile');
                  setDropdownOpen(false);
                }}
              >
                <User size={16} />
                <span>Profile</span>
              </button>
              <button
                className="dropdown-item"
                onClick={() => {
                  navigate('/dashboard/settings');
                  setDropdownOpen(false);
                }}
              >
                <Settings size={16} />
                <span>Settings</span>
              </button>
              <div className="dropdown-divider" />
              <button className="dropdown-item logout" onClick={handleLogout}>
                <LogOut size={16} />
                <span>Logout</span>
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Sign-out confirmation.
          Portalled to <body> rather than rendered in place: `position: fixed`
          is resolved against the nearest ancestor with a transform, filter or
          will-change, so a dialog left inside the header would silently stop
          centering the moment any wrapper gained one. The portal makes
          viewport-centering independent of where this component sits. */}
      {logoutConfirmOpen &&
        createPortal(
          <div
            className="logout-confirm-backdrop"
            onClick={() => setLogoutConfirmOpen(false)}
            role="presentation"
          >
            <div
              className="logout-confirm-card"
              role="dialog"
              aria-modal="true"
              aria-labelledby="logout-confirm-title"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="logout-confirm-icon">
                <LogOut size={24} />
              </div>
              <h3 id="logout-confirm-title">Log out</h3>
              <p>Are you sure you want to Log out of MaxelPay?</p>
              <div className="logout-confirm-actions">
                <button
                  type="button"
                  className="logout-confirm-cancel"
                  onClick={() => setLogoutConfirmOpen(false)}
                >
                  Cancel
                </button>
                <button type="button" className="logout-confirm-submit" onClick={confirmLogout}>
                  Log out
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}
    </header>
  );
};

export default Header;
